import { connectRouter, routerMiddleware } from 'connected-react-router';
import { applyMiddleware, compose, legacy_createStore } from 'redux';
import thunkMiddleware from 'redux-thunk';

import { feSidebarAppointmentClear, feSidebarClose, feSidebarDim, feSidebarOpen } from 'actions/ui.actions';
import { LT_AP_WZ, LT_RDX } from 'bl/loggers.bl';
import CONF from 'config/conf.singleton';
import FLAG from 'config/flag.singleton';
import HISTORY from 'config/history.singleton';
import PUSHER from 'config/pusher.singleton';
import SEGMENT from 'config/segment.singleton';
import SENTRY from 'config/sentry.singleton';
import apiMiddleware from 'middleware/api.middleware';
import rootReducer from 'reducers/root.reducer';
import { rcmSetupCognito } from 'utils/cognito.util';
import { isEnvCy } from 'utils/env/determine.util';
import logger from 'utils/logger.util';
import owns from 'utils/objects/owns.util';
import isFn from 'utils/predicates/function/isFn.util';

const CTAG = 'STORE';
const L = logger([CTAG, LT_RDX]);
const L_SB = logger([CTAG, LT_AP_WZ]);

const SIDEBAR_LOGGED_TYPES = Object.freeze([feSidebarClose.AT.name, feSidebarOpen.AT.name, feSidebarDim.AT.name, feSidebarAppointmentClear.AT.name]);

const mwSidebarLog = () => (next) => (action) => {
  if (!SIDEBAR_LOGGED_TYPES.includes(action?.type)) return next(action);

  const atag = action?.ATAG;
  const data = action?.data;
  const tag = data?.tag;

  L_SB.debug('mwSidebarLog()', atag, tag, data);
  return next(action);
};

const mwGeneralLog = (store) => {
  const FTAG = 'mwGeneralLog()';

  const emitLog = async (options) => {
    const { action, actionType, prev, result } = options ?? {};
    const isPromise = result instanceof Promise;

    const info = { ...options };
    try {
      info.item = isPromise ? await result : result;
      info.type = isPromise ? 'value' : 'result';
    } catch (e) {
      info.item = e;
      info.type = 'error';
    }
    info.next = store.getState(); // must be after the await keyword

    L.debug(FTAG, '🎬', actionType, '✂️', 'p:', prev, 'n:', info?.next, 'a:', action, `${info?.type}:`, info?.item);
  };

  return (next) => (action) => {
    // SEE: https://redux.js.org/tutorials/fundamentals/part-4-store#your-first-custom-middleware

    if (!FLAG.loggerStore) return next(action);

    const actionType = action?.type ?? action?.AUTH_API?.types;
    L.debug(FTAG, '🎬', actionType, '✂️', 'action:', action);

    const prev = store.getState();
    const result = next(action);

    L.unignore(FTAG, 'emitLog:', emitLog({ action, actionType, prev, result }));

    return result;
  };
};

const determineEnhancersComposer = () => {
  const { __REDUX_DEVTOOLS_EXTENSION_COMPOSE__: rdteCompose, __REDUX_DEVTOOLS_EXTENSION__: rdteConfigure } = window;
  if (isFn(rdteConfigure)) rdteConfigure({ serialize: CONF.reduxDevtoolsSerialize });
  return isFn(rdteCompose) ? rdteCompose : compose;
};

const composeEnhancers = determineEnhancersComposer();

const STORE = legacy_createStore(
  connectRouter(HISTORY)(rootReducer),
  undefined,
  composeEnhancers(applyMiddleware(thunkMiddleware, mwSidebarLog, mwGeneralLog, apiMiddleware, routerMiddleware(HISTORY), SEGMENT.reduxTracker), SENTRY.reduxEnhancer),
);

if (CONF.cognitoConfig) {
  rcmSetupCognito(STORE, CONF.cognitoConfig);
  PUSHER.redux.configure({
    store: STORE,
    apiKey: CONF.pusherId,
    authEndpoint: `${CONF.apiRoot}websocket/`,
    cluster: 'eu',
    authTransport: 'jsonp',
    encrypted: true,
  });
}

if (isEnvCy()) {
  // we are running in Cypress, expose the redux store
  window.reduxStore = STORE;
  // eslint-disable-next-line no-prototype-builtins
  if (owns('cypressStart', window)) {
    window.cypressStart();
  }
}

// noinspection JSUnusedGlobalSymbols
export default STORE;
